import { MapContainer, Marker, Popup, TileLayer, Tooltip } from "react-leaflet";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, RISK_MARKER_COLORS, createRiskMarkerIcon } from "@/lib/leaflet-setup";
import {
  assetHazardLabel,
  HORIZON_LABELS,
  seasonTagText,
  getAssetHazardCells,
  getResultsForAsset,
  getWorstRiskForAsset,
  type AssetHazardResult,
} from "@/lib/results";
import { LEVEL_BADGE_CLASSES, LEVEL_LABELS } from "@/lib/level-badge";
import { PendingScore } from "./PendingScore";
import { NoDataScore } from "./NoDataScore";
import { isSingleMainSeason, SeasonTag } from "./StatusCell";
import type { Asset } from "@/lib/assets";
import { HAZARD_TYPES, type TimeHorizon } from "@shared/scoring/types";

interface SiteMapProps {
  assets: Asset[];
  results: AssetHazardResult[];
  horizon: TimeHorizon;
  activeHorizons: TimeHorizon[];
  onHorizonChange: (horizon: TimeHorizon) => void;
}

const LEGEND_ITEMS: { risk: 1 | 2 | 3 | 4; label: string }[] = [
  { risk: 1, label: "Low" },
  { risk: 2, label: "Medium" },
  { risk: 3, label: "High" },
  { risk: 4, label: "Very High" },
];

export function SiteMap({ assets, results, horizon, activeHorizons, onHorizonChange }: SiteMapProps) {
  const withCoords = assets.filter((a) => a.latitude !== null && a.longitude !== null);
  const missingCount = assets.length - withCoords.length;
  const countrySummary = Array.from(new Set(withCoords.map((a) => a.country))).sort().join(" · ");

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <p className="text-sm font-semibold leading-5">Site locations</p>
            {countrySummary && (
              <p className="text-xs leading-4 text-muted-foreground">{countrySummary}</p>
            )}
          </div>
          <span className="rounded-sm border border-border bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
            {withCoords.length} mapped
          </span>
          {activeHorizons.length > 1 && (
            <Select value={horizon} onValueChange={(v) => onHorizonChange(v as TimeHorizon)}>
              <SelectTrigger className="h-7 w-28 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {activeHorizons.map((h) => (
                  <SelectItem key={h} value={h}>
                    {HORIZON_LABELS[h]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {LEGEND_ITEMS.map(({ risk, label }) => (
            <span key={risk} className="flex items-center gap-1">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ background: RISK_MARKER_COLORS[risk] }}
              />
              {label}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-400" />
            Pending
          </span>
        </div>
      </div>
      <div className="h-72 w-full overflow-hidden rounded-md border">
        <MapContainer center={DEFAULT_MAP_CENTER} zoom={DEFAULT_MAP_ZOOM} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {withCoords.map((asset) => {
            const worstRisk = getWorstRiskForAsset(asset.id, results);
            const assetResults = getResultsForAsset(asset.id, results);
            // One line per hazard; Changing Precipitation's MAM and OND share its line
            // (a Malawi site has one main-rainy-season row, named in the label).
            const hazardLines = HAZARD_TYPES.map((hazardType) => getAssetHazardCells(asset.id, hazardType, assetResults)).filter(
              (cells) => cells.length > 0,
            );

            return (
              <Marker
                key={asset.id}
                position={[Number(asset.latitude), Number(asset.longitude)]}
                icon={createRiskMarkerIcon(worstRisk)}
              >
                <Tooltip permanent direction="top" offset={[0, -12]} className="!rounded-md !border-border !px-2 !py-1 !text-xs !font-medium">
                  {asset.name}
                </Tooltip>
                <Popup>
                  <div className="flex flex-col gap-2">
                    <div>
                      <p className="font-medium">{asset.name}</p>
                      <p className="text-xs text-muted-foreground">Importance {asset.businessImportance}</p>
                    </div>
                    <table className="text-xs">
                      <thead>
                        <tr className="text-muted-foreground">
                          <th className="pr-2 py-0.5 text-left font-normal">Hazard</th>
                          <th className="pr-2 py-0.5 text-left font-normal">Vulnerability</th>
                          <th className="py-0.5 text-left font-normal">Risk</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hazardLines.map((cells) => {
                          const r = cells[0];
                          const exposureLine = cells
                            .filter((c) => c.hazardScore !== undefined)
                            .map((c) => `${HORIZON_LABELS[horizon]} Exposure${c.season ? ` ${seasonTagText(c.season)}` : ""}: ${c.hazardScore}/4`)
                            .join(", ");
                          // Future drought rows only: the three scores behind that Exposure.
                          const drought = r.hazardType === "drought" && horizon !== "historical" ? r.droughtDetail : undefined;
                          const droughtLine = drought
                            ? `Current ${drought.currentScore ?? "n/a"} · Change ${drought.changeScore ?? "n/a"} · Combined ${drought.combinedScore ?? "n/a"}`
                            : "";
                          const exposureTitle = [exposureLine, droughtLine].filter(Boolean).join("\n");
                          return (
                            <tr key={r.hazardType}>
                              <td className="pr-2 py-0.5 text-muted-foreground" title={exposureTitle || undefined}>
                                {assetHazardLabel(r.hazardType, cells)}
                              </td>
                              <td className="pr-2 py-0.5">
                                {r.vulnerability !== undefined ? (
                                  <Badge className={LEVEL_BADGE_CLASSES[r.vulnerability]}>{LEVEL_LABELS[r.vulnerability]}</Badge>
                                ) : (
                                  <PendingScore label="Pending" />
                                )}
                              </td>
                              <td className="py-0.5">
                                <span className="inline-flex flex-wrap items-center gap-1">
                                  {cells.map((c) => (
                                    <span key={c.season ?? "all"} className="inline-flex items-center gap-1">
                                      {!isSingleMainSeason(cells) && <SeasonTag season={c.season} />}
                                      {c.status === "computed" && c.risk !== undefined ? (
                                        <Badge className={LEVEL_BADGE_CLASSES[c.risk]}>{LEVEL_LABELS[c.risk]}</Badge>
                                      ) : c.status === "no_data" && c.statusNote ? (
                                        // e.g. a dry-site precipitation season: say why, not "Pending"
                                        <NoDataScore reason={c.statusNote} />
                                      ) : (
                                        <PendingScore label="Pending" />
                                      )}
                                    </span>
                                  ))}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-muted-foreground">
                      Vulnerability combines this site's business importance with the 180Pi physical-impact default
                      for its asset type.
                    </p>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
      {missingCount > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {missingCount} site{missingCount === 1 ? "" : "s"} missing coordinates, not shown on the map.
        </p>
      )}
    </div>
  );
}
